/**
 * ChamadosInner — listagem de chamados com escopo hierarquico (own/org/global).
 *
 * Spec: docs/CHAMADOS_VISIBILIDADE_SPEC.md §9.4.1 + §19.2 G-N11
 *
 * Caracteristicas-chave:
 *  - useScope() resolve escopo efetivo (URL > localStorage > default).
 *  - useChamadosList({ scope, filters }) roteia para o endpoint correto.
 *  - URL state via useSearchParams: filtros (status/priority/tenant_ids/q/...)
 *    sincronizam com query string para deep-link e back/forward do browser.
 *  - G-N11: useEffect([search], () => setSearchInput(search)) sincroniza o
 *    campo de busca interno quando a URL muda externamente.
 *  - Sem coluna de acoes: aprovar e fechar acontecem no detalhe (spec 2026-09-27 §7).
 *  - Empty state diferenciado por scope (own/org/global).
 *  - Coluna Tenant/Solicitante visivel apenas em scope > 'own'.
 *  - Filtro Tenant (MultiSelect) so em scope='global' (useDevOpsTenantList).
 *  - Filtro Org (Select) so em scope='org' e usuario em >1 organizacao.
 */
import { Fragment, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Stack,
  Group,
  Button,
  TextInput,
  Select,
  MultiSelect,
  Table,
  Text,
  Badge,
  Pagination,
  Box,
  Alert,
  Loader,
} from '@mantine/core'
import { useDebouncedValue } from '@mantine/hooks'
import {
  IconAlertCircle,
  IconPlus,
  IconRefresh,
  IconSearch,
  IconChevronRight,
  IconClipboardList,
  IconBuilding,
} from '@tabler/icons-react'
import { useScope } from '@/hooks/useScope'
import { useChamadosList, useSubTasks, type ChamadosListFilters } from '@/hooks/useChamadosList'
import type { SubTaskItem } from '@/api/chamados'
import classes from './Subtasks.module.css'
import { useQuery } from '@tanstack/react-query'
import { useUserOrganizations } from '@/hooks/useUserOrganizations'
import { useDevOpsTenantList } from '@/hooks/useDevOpsTenantList'
import { organizationsApi } from '@/api/organizations'
import { useAuthStore } from '@/store/auth'
import {
  ScopeAppliedBadge,
  ScopeSelector,
  opcoesDoFiltroDeStatus,
  OPCOES_DO_TIPO_DE_CHAMADO,
  TenantBadge,
  RequesterCell,
  PriorityBadge,
  StatusBadge,
  ProtocolNumberBadge,
} from '@/components/chamados'
import { DataCard } from '@/components/ui'
import { agruparPorOrganizacao, type LinhaDaLista } from './agruparPorOrganizacao'

const PAGE_SIZE = 20

const PRIORITY_OPTIONS = [
  { value: 'critical', label: 'Crítica' },
  { value: 'high', label: 'Alta' },
  { value: 'medium', label: 'Média' },
  { value: 'low', label: 'Baixa' },
]

/**
 * SubTaskRows — linhas-filhas inseridas sob o pai quando expandido. Carrega
 * as sub-tarefas sob demanda via useSubTasks e as renderiza indentadas.
 */
function SubTaskRows({
  tenantId,
  parentId,
  colSpan,
  showTenantColumn,
}: {
  tenantId: number
  parentId: number
  colSpan: number
  showTenantColumn: boolean
}) {
  const navigate = useNavigate()
  const { data, isLoading } = useSubTasks(tenantId, parentId, true)

  if (isLoading) {
    return (
      <Table.Tr className={classes.childRow}>
        <Table.Td colSpan={colSpan}>
          <Group gap="xs" pl={40}>
            <Loader size="xs" />
            <Text size="xs" c="dimmed">
              Carregando sub-tarefas…
            </Text>
          </Group>
        </Table.Td>
      </Table.Tr>
    )
  }

  const subs: SubTaskItem[] = data ?? []
  if (subs.length === 0) {
    return (
      <Table.Tr className={classes.childRow}>
        <Table.Td colSpan={colSpan}>
          <Text size="xs" c="dimmed" pl={40}>
            Sem sub-tarefas visíveis.
          </Text>
        </Table.Td>
      </Table.Tr>
    )
  }

  return (
    <>
      {subs.map((s, i) => (
        <Table.Tr
          key={s.id}
          className={classes.childRow}
          style={{ cursor: 'pointer' }}
          onClick={() => navigate(`/chamados/${s.id}`)}
        >
          <Table.Td onClick={(e) => e.stopPropagation()}>
            <div className={classes.childProto}>
              <span className={classes.connector}>
                {i === subs.length - 1 ? '└─' : '├─'}
              </span>
              <span className={classes.childProtoText}>
                {s.protocol_number ?? `#${s.id}`}
              </span>
            </div>
          </Table.Td>
          <Table.Td onClick={(e) => e.stopPropagation()}>
            <Link to={`/chamados/${s.id}`} className={classes.childTitle}>
              {s.title}
            </Link>
            {s.assignee_name && (
              <div className={classes.childSub}>Responsável: {s.assignee_name}</div>
            )}
          </Table.Td>
          {showTenantColumn && <Table.Td />}
          {showTenantColumn && <Table.Td />}
          <Table.Td>
            <StatusBadge status={s.status} withIcon />
          </Table.Td>
          <Table.Td>
            {s.priority ? (
              <PriorityBadge priority={s.priority as 'low' | 'medium' | 'high' | 'critical'} />
            ) : (
              <Text size="xs" c="dimmed">
                —
              </Text>
            )}
          </Table.Td>
          <Table.Td>
            {s.team_name ? (
              <Badge variant="light" color="indigo" size="sm">
                {s.team_name}
              </Badge>
            ) : (
              <Text size="xs" c="dimmed">
                —
              </Text>
            )}
          </Table.Td>
          <Table.Td>
            <Text size="xs" c="dimmed">
              {new Date(s.created_at).toLocaleString('pt-BR')}
            </Text>
          </Table.Td>
        </Table.Tr>
      ))}
    </>
  )
}

export function ChamadosInner() {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const { scope, setScope, allowedScopes, isResolving } = useScope()
  const [searchParams, setSearchParams] = useSearchParams()

  // ── URL state ──────────────────────────────────────────────────────────────
  const search = searchParams.get('q') ?? ''
  const status = searchParams.get('status') ?? ''
  const priority = searchParams.get('priority') ?? ''
  const tenantIdsCsv = searchParams.get('tenant_ids') ?? ''
  const orgIdParam = searchParams.get('organization_id')
  const assigneeIdParam = searchParams.get('assignee_id') // drill-down do Dashboard + filtro org (SP4)
  const requestType = searchParams.get('request_type') ?? '' // filtro "Fila da Organização" (SP4)
  const page = Number(searchParams.get('page') ?? '1')

  // ── G-N11: sincronizar campo interno com URL externa ─────────────────────
  const [searchInput, setSearchInput] = useState(search)
  useEffect(() => {
    setSearchInput(search)
  }, [search])

  const [debouncedSearch] = useDebouncedValue(searchInput, 400)

  // Aplica busca debounced na URL
  useEffect(() => {
    if (debouncedSearch === search) return
    const next = new URLSearchParams(searchParams)
    if (debouncedSearch) next.set('q', debouncedSearch)
    else next.delete('q')
    next.set('page', '1')
    setSearchParams(next, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  // ── Listas auxiliares para filtros ───────────────────────────────────────
  const { data: orgs = [] } = useUserOrganizations()
  const { data: devopsTenants = [] } = useDevOpsTenantList({
    enabled: scope === 'global',
  })

  // organization_id efetivo: URL > primeira org disponivel (quando scope=org)
  const resolvedOrgId = useMemo<number | undefined>(() => {
    if (orgIdParam) return Number(orgIdParam)
    if (scope === 'org' && orgs.length > 0) return orgs[0].id
    return undefined
  }, [orgIdParam, scope, orgs])

  // Atendentes da organização (SP4) — fonte do filtro "Atendente" na visão org.
  // Só carrega quando estamos em scope=org com org resolvida.
  const { data: orgUsers = [] } = useQuery({
    queryKey: ['org-users', resolvedOrgId],
    queryFn: () => organizationsApi.listOrgUsers(resolvedOrgId as number),
    enabled: scope === 'org' && !!resolvedOrgId,
    staleTime: 60_000,
  })

  const assigneeOptions = useMemo(() => {
    const seen = new Set<number>()
    return orgUsers
      .filter((u) => (seen.has(u.user_id) ? false : (seen.add(u.user_id), true)))
      .map((u) => ({ value: String(u.user_id), label: u.full_name || u.email }))
  }, [orgUsers])

  // MultiSelect de tenants — dedup defensivo em tempo de render.
  // Hook + backend ja deduplicam, mas PersistQueryClient pode hidratar cache
  // antigo (pre-fix) do localStorage no primeiro render, antes do queryFn
  // re-executar. Sem dedup aqui, Mantine MultiSelect quebra a tela inteira
  // com "Duplicate options" (incidente 2026-05-18 com tenant 113 em N orgs).
  const tenantOptions = useMemo(() => {
    const seen = new Set<number>()
    return devopsTenants
      .filter((t) => (seen.has(t.id) ? false : (seen.add(t.id), true)))
      .map((t) => ({ value: String(t.id), label: t.name }))
  }, [devopsTenants])

  // ── Build filters para useChamadosList ───────────────────────────────────
  const filters: ChamadosListFilters = useMemo(() => {
    const tenantIds = tenantIdsCsv
      ? tenantIdsCsv
          .split(',')
          .map((v) => Number(v))
          .filter((n) => Number.isFinite(n) && n > 0)
      : undefined

    const assigneeId = assigneeIdParam
      ? (assigneeIdParam === 'me' ? 'me' : Number(assigneeIdParam))
      : undefined

    return {
      q: search || undefined,
      status: status ? [status] : undefined,
      priority: priority ? [priority] : undefined,
      request_type: requestType || undefined,
      tenant_ids: tenantIds,
      organization_id: resolvedOrgId,
      assignee_id: assigneeId as number | 'me' | undefined,
      // category_id: NÃO exposto na visão org — categorias são por-tenant
      // (GET /tenants/{id}/ticket-categories) e a org abrange N tenants;
      // sem fonte de categorias org-wide. TODO: filtro de categoria quando
      // houver endpoint org-wide de categorias.
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    }
  }, [search, status, priority, requestType, tenantIdsCsv, resolvedOrgId, assigneeIdParam, page])

  // "Minha organização" para quem é cross-org (DevOps/admin que enxerga global):
  // em vez da org única (vazia), mostra TODOS os chamados agrupados por org.
  const isCrossOrg = allowedScopes.includes('global')
  const groupByOrg = scope === 'org' && isCrossOrg
  const effectiveScope = groupByOrg ? 'global' : scope

  const { data, isLoading, error, refetch, isFetching } = useChamadosList({
    scope: effectiveScope,
    filters,
    enabled: !isResolving,
  })

  const items = data?.items ?? []
  const total = data?.total ?? 0
  const showTenantColumn = scope !== 'own'

  // Sub-tarefas colapsáveis: ids de pais expandidos (local à tela).
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const toggleExpanded = (id: number) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  // ── Helpers para mutar URL ───────────────────────────────────────────────
  const updateParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams)
    if (value && value.length > 0) next.set(key, value)
    else next.delete(key)
    next.set('page', '1')
    setSearchParams(next)
  }

  const setPage = (p: number) => {
    const next = new URLSearchParams(searchParams)
    next.set('page', String(p))
    setSearchParams(next)
  }

  // Trocar de escopo limpa o que só tem campo num escopo (tenant em global; organização,
  // atendente e tipo em org) e a página: esses parâmetros vão para a API em qualquer escopo,
  // e sem o campo visível virariam filtro escondido. Busca, status e prioridade ficam.
  // O menu antigo não tinha o problema porque cada item abria a URL limpa.
  const trocarEscopo = (s: typeof scope) => {
    setScope(s, { syncUrl: false })
    const next = new URLSearchParams(searchParams)
    for (const k of ['tenant_ids', 'organization_id', 'assignee_id', 'request_type', 'page']) next.delete(k)
    next.set('scope', s)
    setSearchParams(next)
  }

  // Linhas do corpo: no modo cross-org "Minha organização", intercala cabeçalhos de
  // organização; senão lista plana.
  const rowEntries: LinhaDaLista<typeof items[number]>[] = groupByOrg
    ? agruparPorOrganizacao(items, total)
    : items.map((item) => ({ type: 'item' as const, item }))

  // Empty state diferenciado por scope (4 variantes incluindo "filtered")
  const hasActiveFilter = !!(search || status || priority || tenantIdsCsv || assigneeIdParam || requestType)
  const emptyMessage = hasActiveFilter
    ? 'Nenhum chamado encontrado com os filtros aplicados.'
    : scope === 'own'
    ? 'Você ainda não criou chamados.'
    : scope === 'org'
    ? 'Nenhum chamado nesta organização.'
    : 'Nenhum chamado encontrado.'

  // Colunas fixas: Protocolo, Título, Status, Prioridade, Time, Criado = 6
  // + Tenant + Solicitante quando showTenantColumn = 8
  const colSpan = showTenantColumn ? 8 : 6

  return (
    <Stack gap="md">
      {error && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          Não foi possível carregar os chamados. Tente novamente em instantes.
        </Alert>
      )}

      <Group justify="space-between" align="flex-start" wrap="wrap">
        <Group gap="sm" align="center" wrap="nowrap">
          <span
            style={{
              display: 'grid',
              placeItems: 'center',
              width: 40,
              height: 40,
              borderRadius: 'var(--mantine-radius-md)',
              color: '#fff',
              background: 'var(--ai-brand-gradient)',
              boxShadow: 'var(--ai-shadow-glow)',
              flexShrink: 0,
            }}
          >
            <IconClipboardList size={22} />
          </span>
          <Stack gap={4}>
            {/* O titulo vem do layout desde 2026-08-13 (routeHeader + PageHeader). Estava aqui
          tambem, repetindo o nome da rota. */}
            <Group gap="xs">
              {/* O escopo volta a ser escolhido aqui: o menu tem um item só, "Todos os
                  chamados" (spec 2026-10-21-chamados-menu-todos-os-chamados). Com um escopo
                  só não há o que escolher, e o selo ao lado já o diz. */}
              {!isResolving && allowedScopes.length > 1 && (
                <ScopeSelector scope={scope} setScope={trocarEscopo} allowedScopes={allowedScopes} />
              )}
              {data?.applied_scope && (
                <ScopeAppliedBadge
                  scope={data.applied_scope}
                  downgradeReason={data.downgrade_reason}
                  orgName={
                    data.organization_id
                      ? orgs.find((o) => o.id === data.organization_id)?.name ?? null
                      : null
                  }
                />
              )}
            </Group>
          </Stack>
        </Group>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => navigate('/chamados/novo')}
        >
          Novo chamado
        </Button>
      </Group>

      <Group gap="xs" wrap="wrap">
        <TextInput
          placeholder="Buscar..."
          leftSection={<IconSearch size={14} />}
          value={searchInput}
          onChange={(e) => setSearchInput(e.currentTarget.value)}
          style={{ minWidth: 240 }}
          aria-label="Buscar chamados por texto"
        />
        <Select
          placeholder="Status"
          data={opcoesDoFiltroDeStatus()}
          value={status || null}
          onChange={(v) => updateParam('status', v)}
          clearable
          aria-label="Filtrar por status"
        />
        <Select
          placeholder="Prioridade"
          data={PRIORITY_OPTIONS}
          value={priority || null}
          onChange={(v) => updateParam('priority', v)}
          clearable
          aria-label="Filtrar por prioridade"
        />
        {scope === 'org' && orgs.length > 1 && (
          <Select
            placeholder="Organização"
            data={orgs.map((o) => ({ value: String(o.id), label: o.name }))}
            value={orgIdParam}
            onChange={(v) => updateParam('organization_id', v)}
            aria-label="Selecionar organização"
          />
        )}
        {/* SP4 — Fila da Organização: filtros por atendente e tipo de chamado. */}
        {scope === 'org' && (
          <>
            <Select
              placeholder="Atendente"
              data={assigneeOptions}
              value={assigneeIdParam}
              onChange={(v) => updateParam('assignee_id', v)}
              searchable
              clearable
              style={{ minWidth: 200 }}
              aria-label="Filtrar por atendente"
            />
            <Select
              placeholder="Tipo"
              data={OPCOES_DO_TIPO_DE_CHAMADO}
              value={requestType || null}
              onChange={(v) => updateParam('request_type', v)}
              clearable
              aria-label="Filtrar por tipo de chamado"
            />
          </>
        )}
        {scope === 'global' && (
          <MultiSelect
            placeholder="Tenants"
            data={tenantOptions}
            value={tenantIdsCsv ? tenantIdsCsv.split(',') : []}
            onChange={(v) =>
              updateParam('tenant_ids', v.length ? v.join(',') : null)
            }
            searchable
            clearable
            style={{ minWidth: 240 }}
            aria-label="Filtrar por tenants"
          />
        )}
      </Group>

      <DataCard flush
        title={scope === 'own' ? 'Meus chamados' : scope === 'org' ? 'Minha organização' : 'Todos'}
        badge={{ value: total }}
        refreshable
        refreshing={isFetching}
        onRefresh={() => refetch()}
        pagination={{ page, total, pageSize: PAGE_SIZE, onChange: setPage }}
      >
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Protocolo</Table.Th>
              <Table.Th>Título</Table.Th>
              {showTenantColumn && <Table.Th>Tenant</Table.Th>}
              {showTenantColumn && <Table.Th>Solicitante</Table.Th>}
              <Table.Th>Status</Table.Th>
              <Table.Th>Prioridade</Table.Th>
              <Table.Th>Time</Table.Th>
              <Table.Th>Criado</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {isLoading && (
              <Table.Tr>
                <Table.Td colSpan={colSpan}>
                  <Text ta="center" c="dimmed" py="md">
                    Carregando...
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {!isLoading && items.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={colSpan}>
                  <Text ta="center" c="dimmed" py="xl">
                    {emptyMessage}
                  </Text>
                </Table.Td>
              </Table.Tr>
            )}
            {!isLoading &&
              rowEntries.map((entry) => {
                if (entry.type === 'header') {
                  return (
                    <Table.Tr key={`org-${entry.org}`} className={classes.orgHeaderRow}>
                      <Table.Td colSpan={colSpan}>
                        <Group gap="xs">
                          <IconBuilding size={14} style={{ color: 'var(--ai-brand-verde)' }} />
                          <Text fw={700} size="sm">{entry.org}</Text>
                          <Badge size="sm" variant="light" color="gray" radius="sm">{entry.rotulo}</Badge>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  )
                }
                const item = entry.item
                const subCount = item.sub_task_count ?? 0
                const hasSubs = subCount > 0
                const isOpen = expanded.has(item.id)
                const subDone = item.sub_task_done ?? 0
                const pct = subCount > 0 ? Math.round((subDone / subCount) * 100) : 0
                return (
                <Fragment key={item.id}>
                <Table.Tr
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/chamados/${item.id}`)}
                >
                  <Table.Td onClick={(e) => e.stopPropagation()}>
                    <div className={classes.protoCell}>
                      {hasSubs ? (
                        <button
                          type="button"
                          className={classes.chevBtn}
                          data-open={isOpen}
                          aria-expanded={isOpen}
                          aria-label={
                            isOpen
                              ? `Colapsar sub-tarefas de ${item.title}`
                              : `Expandir sub-tarefas de ${item.title}`
                          }
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleExpanded(item.id)
                          }}
                        >
                          <IconChevronRight size={14} />
                        </button>
                      ) : (
                        <span className={classes.chevSpacer} />
                      )}
                      <ProtocolNumberBadge value={item.protocol_number} withCopy />
                    </div>
                  </Table.Td>
                  <Table.Td onClick={(e) => e.stopPropagation()}>
                    <Link to={`/chamados/${item.id}`}>
                      <Text size="sm" c="blue.7" lineClamp={1} maw={300}>
                        {item.title}
                      </Text>
                    </Link>
                    {hasSubs && (
                      <div className={classes.rollup}>
                        <span className={classes.rollupText}>
                          {subCount} {subCount === 1 ? 'sub-tarefa' : 'sub-tarefas'} · {subDone}/{subCount}
                        </span>
                        <span className={classes.bar}>
                          <i style={{ width: `${pct}%` }} />
                        </span>
                      </div>
                    )}
                  </Table.Td>
                  {showTenantColumn && (
                    <Table.Td>
                      <TenantBadge
                        tenantName={item.tenant_name}
                        environment={item.environment}
                      />
                    </Table.Td>
                  )}
                  {showTenantColumn && (
                    <Table.Td>
                      <RequesterCell
                        name={item.requested_by_name}
                        isMe={item.requested_by_user_id === user?.id}
                      />
                    </Table.Td>
                  )}
                  <Table.Td>
                    <StatusBadge status={item.status} withIcon />
                  </Table.Td>
                  <Table.Td>
                    {item.priority ? (
                      <PriorityBadge priority={item.priority as 'low' | 'medium' | 'high' | 'critical'} />
                    ) : (
                      <Badge color="gray" variant="light">—</Badge>
                    )}
                  </Table.Td>
                  <Table.Td>
                    {item.target_template_name ? (
                      <Badge variant="light" color="indigo" size="sm">
                        {item.target_template_name}
                      </Badge>
                    ) : (
                      <Text size="xs" c="dimmed">—</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <Text size="xs" c="dimmed">
                      {new Date(item.created_at).toLocaleString('pt-BR')}
                    </Text>
                  </Table.Td>
                </Table.Tr>
                {hasSubs && isOpen && (
                  <SubTaskRows
                    tenantId={item.tenant_id}
                    parentId={item.id}
                    colSpan={colSpan}
                    showTenantColumn={showTenantColumn}
                  />
                )}
                </Fragment>
                )
              })}
          </Table.Tbody>
        </Table>
      </DataCard>
    </Stack>
  )
}
